/**
 * ─── DAILY RP IDEA ──────────────────────────────────────────────────────────
 *
 * The "what story do you want to live in?" box shipped with three hardcoded
 * examples that never changed. This replaces them, once a day, with a fresh
 * suggestion generated from the player's own writing preferences.
 *
 * Design constraints that shaped this:
 *
 *   • ONE call per user per day, not one per render. The setup panel mounts
 *     every time you open the RP tab, and an unguarded generate-on-mount would
 *     bill a prompt every single visit. The cache key is the local calendar
 *     date, so it rolls at midnight in the player's own timezone rather than
 *     24h after whenever they last happened to look.
 *   • It must NEVER block or break the box. Any failure — no key, offline,
 *     provider down — falls back to the static examples. A suggestion is a
 *     nicety; being able to type is not.
 *   • The generated line is a PLACEHOLDER, not prefilled text. It suggests
 *     without hijacking the field or being accidentally submitted.
 */

const STORAGE_KEY = 'mfml_daily_rp_idea';

/** Local calendar date (not UTC) so the idea turns over at the player's midnight. */
export function todayKey(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Read a cached idea, but only if it was generated for THIS user, today. */
export function readCachedIdea(raw, userId, key = todayKey()) {
  if (!raw) return null;
  try {
    const c = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!c || c.date !== key) return null;
    if (userId && c.userId && c.userId !== userId) return null;
    return typeof c.idea === 'string' && c.idea.trim() ? c.idea : null;
  } catch {
    return null;
  }
}

/**
 * Strip the shapes a model reaches for even when told not to: surrounding
 * quotes, a leading "Idea:" label, a bullet, a trailing period on what should
 * read as a fragment.
 */
export function cleanIdea(text) {
  let s = String(text || '').trim();
  if (!s) return '';
  s = s.split('\n').map(l => l.trim()).filter(Boolean)[0] || '';
  s = s.replace(/^(idea|prompt|suggestion|story)\s*[:\-–]\s*/i, '');
  s = s.replace(/^[-•*]\s*/, '');
  s = s.replace(/^["“'](.+)["”']$/s, '$1');
  return s.trim().slice(0, 240);
}

export function buildIdeaPrompt({ profile = '', storyPrefs = '', recentThemes = [] } = {}) {
  const bits = [];
  // 30k guard, not a working cap: the full three-layer profile (stated +
  // deep + weekly, ~24k chars) fits whole. The old 1500 silently discarded
  // 94% of it.
  if (profile) bits.push(`WHO THEY ARE AS A WRITER (from their own past writing):\n${String(profile).slice(0, 30000)}`);
  if (storyPrefs) bits.push(`THEIR STATED STORY PREFERENCES:\n${String(storyPrefs).slice(0, 800)}`);
  if (recentThemes.length) bits.push(`RECENT STORIES (suggest something DIFFERENT from these):\n${recentThemes.slice(0, 8).join('\n')}`);
  return `You suggest a single roleplay premise to a writer opening a blank story box.

${bits.join('\n\n') || 'No profile available — suggest something broadly appealing for a supernatural-romance RP setting.'}

Write ONE premise, 12 to 28 words, in the second person or as a scene fragment, the way they would type it themselves. Concrete and specific: a place, a moment, a tension. It should feel like an invitation, not a logline.

Rules:
- Output ONLY the premise. No label, no quotes, no preamble, no alternatives.
- No em dashes.
- Do not name a character unless it appears in their preferences above.
- Make it different in shape and mood from the recent stories listed.`;
}

/** Persist today's idea. Storage failures are non-fatal by design. */
export function cacheIdea(idea, userId, key = todayKey()) {
  const payload = JSON.stringify({ date: key, userId: userId || null, idea });
  try { localStorage.setItem(STORAGE_KEY, payload); } catch { /* private mode */ }
  return payload;
}

export function loadIdeaFromStorage(userId) {
  try { return readCachedIdea(localStorage.getItem(STORAGE_KEY), userId); } catch { return null; }
}

export { STORAGE_KEY };
